import { get, isEqual } from 'lodash';

import type { HotelIngredientFilters } from '@apps/main/modules/recipe/ingredients';

import type {
  FullStars,
  StarsRatingFilter,
} from '@libs/trip-planner-interfaces';
import {
  AccommodationRule,
  AccommodationType,
  AmenitiesFilter,
  BoardBasisFilter,
  ExperienceFilter,
} from '@libs/trip-planner-interfaces';

import type {
  HotelFilter,
  HotelFilterPillData,
  HotelFiltersForm,
} from '../model/hotelFilters';
import {
  ALL_HOTEL_FILTERS,
  DEFAULT_HOTEL_FILTERS,
  HOTEL_FILTER_PILLS_CONFIG,
} from '../model/hotelFilters';

export function getActiveFilters(
  form: HotelFiltersForm
): HotelFilterPillData[] {
  return ALL_HOTEL_FILTERS.filter((filter: HotelFilter) => {
    const currentValue = get(form, filter);
    const defaultValue = get(DEFAULT_HOTEL_FILTERS, filter);
    return !isEqual(currentValue, defaultValue);
  }).map((filter: HotelFilter) => {
    const config = HOTEL_FILTER_PILLS_CONFIG[filter];
    return { type: config.type, text: config.textFactory(form) };
  });
}

export function getHotelFilterFromIngredient(
  hotelIngredientFilters: HotelIngredientFilters
): HotelFiltersForm {
  return {
    hotelName: hotelIngredientFilters.filterText,
    rules: {
      freeCancellation: hotelIngredientFilters.selectedHotelRules.includes(
        AccommodationRule.FreeCancellation
      ),
    },
    typeOfAccommodation: {
      hotel: hotelIngredientFilters.selectedHotelTypes.includes(
        AccommodationType.Hotel
      ),
      apartment: hotelIngredientFilters.selectedHotelTypes.includes(
        AccommodationType.Apartment
      ),
      houseOrCottage: hotelIngredientFilters.selectedHotelTypes.includes(
        AccommodationType.HouseOrCottage
      ),
    },
    rating: { stars: getFullStarsArray(hotelIngredientFilters.starRating) },
    meals: {
      noMeals: hotelIngredientFilters.boardBasis.includes(
        BoardBasisFilter.RoomOnly
      ),
      breakfast: hotelIngredientFilters.boardBasis.includes(
        BoardBasisFilter.Breakfast
      ),
      halfBoard: hotelIngredientFilters.boardBasis.includes(
        BoardBasisFilter.HalfBoard
      ),
      fullBoard: hotelIngredientFilters.boardBasis.includes(
        BoardBasisFilter.FullBoard
      ),
      allInclusive: hotelIngredientFilters.boardBasis.includes(
        BoardBasisFilter.AllInclusive
      ),
    },
    amenities: {
      pool: hotelIngredientFilters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Pool
      ),
      parking: hotelIngredientFilters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Parking
      ),
      spa: hotelIngredientFilters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Spa
      ),
      gym: hotelIngredientFilters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Gym
      ),
      wifi: hotelIngredientFilters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.Wifi
      ),
      airConditioned: hotelIngredientFilters.selectedAmenitiesFilters.includes(
        AmenitiesFilter.AirConditioned
      ),
      vehicleChargingStation:
        hotelIngredientFilters.selectedAmenitiesFilters.includes(
          AmenitiesFilter.VehicleChargingStation
        ),
    },
    experience: {
      business: hotelIngredientFilters.selectedExperienceFilters.includes(
        ExperienceFilter.Business
      ),
      family: hotelIngredientFilters.selectedExperienceFilters.includes(
        ExperienceFilter.Family
      ),
      petsAllowed: hotelIngredientFilters.selectedExperienceFilters.includes(
        ExperienceFilter.PetsAllowed
      ),
      greenSustainableProperty:
        hotelIngredientFilters.selectedExperienceFilters.includes(
          ExperienceFilter.GreenSustainableProperty
        ),
    },
  };
}

export function getFullStarsArray(start: StarsRatingFilter): FullStars[] {
  const maxStars = 5;
  const result: FullStars[] = [];
  const startBound: FullStars = start === 0 ? 1 : start;

  for (let i = startBound; i <= maxStars; i++) {
    result.push(i);
  }

  return result;
}
